/** @param {import('firebase/firestore').Timestamp | Date | number | string | null | undefined} v */
export function toDateTime(v) {
  if (v == null) return null;
  if (typeof v?.toDate === "function") return v.toDate();
  if (v instanceof Date) return new Date(v.getTime());
  if (typeof v === "number") return new Date(v);
  if (typeof v === "string") {
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

export function stripMillis(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes(), d.getSeconds());
}

/** One punch instant per document — matches Flutter DailyLateReport (timestamp → date → punchIn). */
export function extractPrimaryPunchTime(data) {
  if (!data || typeof data !== "object") return null;
  return toDateTime(data.timestamp) || toDateTime(data.date) || toDateTime(data.punchIn) || null;
}

export function collectPunchTimes(data) {
  const set = new Map();
  const add = (v) => {
    const dt = toDateTime(v);
    if (dt) set.set(stripMillis(dt).getTime(), stripMillis(dt));
  };
  add(data?.timestamp);
  add(data?.time);
  const raw = data?.raw;
  if (raw && typeof raw === "object") {
    add(raw.timestamp);
    add(raw.time);
  }
  return Array.from(set.values()).sort((a, b) => a.getTime() - b.getTime());
}

export function mergeAndDedupeTimestamps(tsList, thresholdSeconds = 10) {
  const list = [...tsList].sort((a, b) => a.getTime() - b.getTime());
  if (list.length === 0) return [];
  const out = [list[0]];
  let current = list[0];
  for (let i = 1; i < list.length; i++) {
    const dt = list[i];
    const diff = Math.abs((dt.getTime() - current.getTime()) / 1000);
    if (diff > thresholdSeconds) {
      out.push(dt);
      current = dt;
    }
  }
  return out;
}

export function computeSessionsFromTimestamps(timestamps) {
  const t = [...timestamps].sort((a, b) => a.getTime() - b.getTime());
  const sessions = [];
  let totalMs = 0;
  let partial = false;
  for (let i = 0; i < t.length; i += 2) {
    if (i + 1 < t.length) {
      const start = t[i];
      const end = t[i + 1];
      if (end > start) {
        sessions.push({ start, end });
        totalMs += end - start;
      } else {
        partial = true;
      }
    } else {
      partial = true;
    }
  }
  return { sessions, total: totalMs, partial };
}

export function formatDurationHMM(ms) {
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  return `${h}:${String(m).padStart(2, "0")}`;
}

export function msToDuration(ms) {
  return { ms, hours: ms / 3600000 };
}

/** Attributed time inside [startOfDay, endOfDay) for each session. */
export function attributedMsInDay(sessions, startOfDay, endOfDay) {
  let ms = 0;
  for (const s of sessions) {
    const st = s.start < startOfDay ? startOfDay : s.start;
    const en = s.end > endOfDay ? endOfDay : s.end;
    if (en > st) ms += en - st;
  }
  return ms;
}

/** Paid-day length used for OT (same as Daily hours). */
export const PAID_DAY_THRESHOLD_MS = 9 * 3600000;

export function otFromAttributedMs(attributedMs) {
  const ms = Number(attributedMs) || 0;
  return Math.max(0, ms - PAID_DAY_THRESHOLD_MS);
}

/** Time below the 9h paid threshold (no OT until this gap is closed). */
export function shortOfPaidDayThresholdMs(attributedMs) {
  const ms = Number(attributedMs) || 0;
  return Math.max(0, PAID_DAY_THRESHOLD_MS - ms);
}

export function pickNumber(data, keys) {
  for (const k of keys) {
    if (data[k] !== undefined && data[k] !== null) {
      const v = data[k];
      if (typeof v === "number" && !isNaN(v)) return v;
      if (typeof v === "string") {
        const d = parseFloat(v);
        if (!isNaN(d)) return d;
      }
    }
    const kl = k.toLowerCase();
    if (data[kl] !== undefined && data[kl] !== null) {
      const v = data[kl];
      if (typeof v === "number" && !isNaN(v)) return v;
      if (typeof v === "string") {
        const d = parseFloat(v);
        if (!isNaN(d)) return d;
      }
    }
  }
  return null;
}

export function pickString(data, keys) {
  for (const k of keys) {
    if (data[k] !== undefined && data[k] !== null) {
      const v = data[k];
      const s = typeof v === "string" ? v.trim() : String(v).trim();
      if (s) return s;
    }
    const kl = k.toLowerCase();
    if (data[kl] !== undefined && data[kl] !== null) {
      const v = data[kl];
      const s = typeof v === "string" ? v.trim() : String(v).trim();
      if (s) return s;
    }
  }
  return null;
}

const ID_KEYS = [
  "employeeId",
  "EmployeeId",
  "empId",
  "empID",
  "employee_id",
  "user_id",
  "userId",
  "employeeid",
  "deviceId",
  "DeviceId",
  "device_id",
  "deviceid",
  "id",
];

export function extractEmpKeyFromData(data, docId) {
  for (const k of ID_KEYS) {
    if (data[k] !== undefined && data[k] !== null) {
      const v = data[k];
      if (typeof v === "string" && v.trim()) return v.trim();
      if (typeof v === "number") return String(v);
    }
    const kl = k.toLowerCase();
    if (data[kl] !== undefined && data[kl] !== null) {
      const v = data[kl];
      if (typeof v === "string" && v.trim()) return v.trim();
      if (typeof v === "number") return String(v);
    }
  }
  return docId;
}

const DEFAULT_WORK_DAYS = 30;
const DEFAULT_HOURS_PER_DAY = 9;

export function hourlyRateFor(meta) {
  if (!meta) return 0;
  if (meta.salaryPerHour != null) return meta.salaryPerHour;
  if (meta.dailySalary != null) return meta.dailySalary / DEFAULT_HOURS_PER_DAY;
  if (meta.monthlySalary != null) {
    const totalHours = DEFAULT_WORK_DAYS * DEFAULT_HOURS_PER_DAY;
    return meta.monthlySalary / totalHours;
  }
  return 0;
}

export function rateBasisFor(meta) {
  if (!meta) return "—";
  if (meta.salaryPerHour != null) return "hr";
  if (meta.dailySalary != null) return "day";
  if (meta.monthlySalary != null) return "month";
  return "—";
}

export function pad2(n) {
  return String(n).padStart(2, "0");
}

/** Local wall-clock ISO fragment for Firestore string timestamp range (matches Flutter). */
export function toLocalIsoBoundary(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
}
